// Unit identity management utilities

const BENCH_PIECE_TYPES = Object.freeze(['wP', 'wN', 'wB', 'wR', 'wQ']);

/**
 * Generate a new unit ID
 * 
 * @param {number} nextUnitId - Current next unit ID counter
 * @returns {string} New unit ID (e.g., 'u1')
 */
export function createUnitId(nextUnitId) {
  return 'u' + (nextUnitId || 1);
}

/**
 * Parse a piece type to get the base form
 * 
 * @param {string} pieceType - Piece type (e.g., 'wK', 'wP_red')
 * @returns {string} Base piece type (e.g., 'wK', 'wP')
 */
export function getBasePieceType(pieceType) {
  const m = String(pieceType || '').match(/^([wb])([pnbrqk])/i);
  return m ? (m[1].toLowerCase() + m[2].toUpperCase()) : pieceType;
}

/**
 * Check if a piece type is the king
 * 
 * @param {string} pieceType - Piece type
 * @returns {boolean} True if king
 */
export function isKingPiece(pieceType) {
  return getBasePieceType(pieceType) === 'wK';
}

/**
 * Compute state updates for ensuring a unit ID exists for a square
 * 
 * @param {Object} state - Current state
 * @param {string} square - Board square
 * @param {string} pieceType - Piece type being placed
 * @returns {{ unitId: string, updates: Object }} The unit ID and state updates
 */
export function computeUnitIdForSquare(state, square, pieceType) {
  const prepMap = { ...(state.prepUnitIdBySquare || {}) };
  
  // If already has a unit ID, return it
  if (prepMap[square]) {
    return { unitId: prepMap[square], updates: null };
  }
  
  const newUnitId = createUnitId(state.nextUnitId);
  const nextUnitId = (state.nextUnitId || 1) + 1;
  prepMap[square] = newUnitId;
  
  const updates = { 
    prepUnitIdBySquare: prepMap, 
    nextUnitId 
  };
  
  // Track king unit ID
  if (isKingPiece(pieceType) && !state.kingUnitId) {
    updates.kingUnitId = newUnitId;
  }
  
  return { unitId: newUnitId, updates };
}

/**
 * Compute the pending bench rewards to apply
 * 
 * @param {Object} pendingBenchRewards - Map of piece type to count
 * @returns {Array<string>} List of piece types to add (with duplicates for count)
 */
export function computePendingRewards(pendingBenchRewards) {
  const rewards = pendingBenchRewards || {};
  const result = [];
  
  BENCH_PIECE_TYPES.forEach((pt) => {
    const count = rewards[pt] || 0;
    for (let i = 0; i < count; i++) {
      result.push(pt);
    }
  });
  
  return result;
}

/**
 * Get a cleared pending rewards object
 * 
 * @returns {Object} Empty pending rewards
 */
export function getEmptyPendingRewards() {
  return { wP: 0, wN: 0, wB: 0, wR: 0, wQ: 0 };
}

/**
 * Check if there are any pending rewards
 * 
 * @param {Object} pendingBenchRewards - Map of piece type to count
 * @returns {boolean} True if any rewards pending
 */
export function hasPendingRewards(pendingBenchRewards) {
  return computePendingRewards(pendingBenchRewards).length > 0;
}






